import { useState, MouseEvent, FC, ChangeEvent } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { useDispatch } from 'react-redux';

import classNames from 'classnames';
import dayjs from 'dayjs';
import { CirclePlus, Trash2, Pencil, ChevronDown, RotateCw, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { BaseCheckbox, BaseMenu, BaseMenuItem } from 'wbs-components-lib';

import { Accordion, AccordionSummary, Tooltip, Typography, Box, AccordionDetails } from '@mui/material';

import { BetEventStatusesEnum } from '@shared/constants';
import { textStyles } from '@shared/styles';
import { BetEventModel } from '@shared/types';

import { useSetBetEventStatusMutation, useSetIsActiveBetEventMutation } from '@services';

import {
  AppDispatch,
  setIsBetEventDeleteModalOpened,
  setIsBetEventEditModalOpened,
  setIsBetEventFinishModalOpened,
  setIsBetEventUnfinishModalOpened,
  setIsBetOutcomeCreateModalOpened,
  setProcessingBetEvent,
  updateBetEvent,
} from '@store';

import { BetSystemEventOutcomes } from './BetSystemEventOutcomes';
import { BetSystemEventItemProps } from './types';
import { getStatusIcon, getTogglerDetails } from './utils';

import styles from './BetSystemEventItem.module.css';

const { defaultText } = textStyles;

const {
  betEventItem,
  actionsContainer,
  createdIcon,
  inProgressIcon,
  completedIcon,
  statusContainer,
  betEventItemDraggable,
} = styles;

export const BetSystemEventItem: FC<BetSystemEventItemProps> = ({
  betEvent,
  handleBetEventOutcomesExpand,
  expandedEventOutcomesId,
  index,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isMenuOpened = Boolean(menuAnchor);

  const [setIsActiveBetEvent] = useSetIsActiveBetEventMutation();
  const [setBetEventStatus] = useSetBetEventStatusMutation();

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const { id: betEventId, status, name: betEventName, startDate, isActive = false } = betEvent;

  const { nextStatus, icon: Icon, text, statusText } = getTogglerDetails(status);

  const handleSetBetEventStatus = async (status: BetEventStatusesEnum) => {
    const updatedBetEvent = await setBetEventStatus({
      betEventId,
      payload: { status },
    }).unwrap();

    dispatch(updateBetEvent(updatedBetEvent));

    toast.success(`Success: Bet event has been ${statusText}!`);
  };

  const isEventFinished = status === BetEventStatusesEnum.FINISHED;
  const isEventCreated = status === BetEventStatusesEnum.CREATED;

  const handleBetEventEditModalOpen = (betEvent: BetEventModel) => {
    dispatch(setProcessingBetEvent(betEvent));

    dispatch(setIsBetEventEditModalOpened(true));
  };

  const handleBetEventDeleteModalOpen = (betEvent: BetEventModel) => {
    dispatch(setProcessingBetEvent(betEvent));

    dispatch(setIsBetEventDeleteModalOpened(true));
  };

  const handleBetEventOutcomeCreateModalOpen = (betEvent: BetEventModel) => {
    dispatch(setProcessingBetEvent(betEvent));

    dispatch(setIsBetOutcomeCreateModalOpened(true));
  };

  const handleBetEventFinishModalOpen = (betEvent: BetEventModel) => {
    dispatch(setProcessingBetEvent(betEvent));

    dispatch(setIsBetEventFinishModalOpened(true));
  };

  const handleBetEventUnfinishModalOpen = (betEvent: BetEventModel) => {
    dispatch(setProcessingBetEvent(betEvent));

    dispatch(setIsBetEventUnfinishModalOpened(true));
  };

  const menuItems: BaseMenuItem[] = [
    {
      icon: <Icon />,
      text,
      onClick: () => handleSetBetEventStatus(nextStatus as BetEventStatusesEnum),
      isDisabled: isEventFinished,
    },
    {
      icon: isEventFinished ? <RotateCw /> : <Trophy />,
      text: isEventFinished ? 'Unfinish' : 'Finish',
      isDisabled: isEventCreated,
      onClick: isEventFinished
        ? () => handleBetEventUnfinishModalOpen(betEvent)
        : () => handleBetEventFinishModalOpen(betEvent),
    },
    {
      icon: <Pencil />,
      text: 'Edit',
      onClick: () => handleBetEventEditModalOpen(betEvent),
    },
    {
      icon: <CirclePlus />,
      text: 'Create Outcome',
      isDisabled: isEventFinished,
      onClick: () => handleBetEventOutcomeCreateModalOpen(betEvent),
    },
    {
      icon: <Trash2 />,
      text: 'Delete',
      onClick: () => handleBetEventDeleteModalOpen(betEvent),
    },
  ];

  const handleActiveEventChange = async (event: ChangeEvent<HTMLInputElement>) => {
    event.stopPropagation();

    const isActive = event.target.checked;

    const updatedBetEvent = await setIsActiveBetEvent({
      betEventId,
      payload: { isActive },
    }).unwrap();

    dispatch(updateBetEvent(updatedBetEvent));

    toast.success('Success: Bet event has been updated!');
  };

  const handleOutcomesExpand = () => {
    const currentBetOutcomesIdExpanded = isOutcomesExpanded ? '' : betEventId;

    handleBetEventOutcomesExpand(currentBetOutcomesIdExpanded);
  };

  const isOutcomesExpanded = expandedEventOutcomesId === betEventId;
  const expandSlotsTitle = `${isOutcomesExpanded ? 'Collapse' : 'Expand'} Outcomes`;
  const formattedStartDate = dayjs(startDate).format('DD-MM-YYYY HH:mm');

  const StatusIcon = getStatusIcon(status);

  return (
    <Draggable key={betEventId} draggableId={betEventId} index={index}>
      {(provided, snapshot) => (
        <Accordion
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
          expanded={isOutcomesExpanded}
          className={classNames({
            [betEventItemDraggable]: snapshot.isDragging,
          })}
        >
          <AccordionSummary
            expandIcon={
              <Tooltip title={expandSlotsTitle}>
                <ChevronDown onClick={handleOutcomesExpand} />
              </Tooltip>
            }
            className={betEventItem}
          >
            <Tooltip title={BetEventStatusesEnum[status]} placement="top-start">
              <Typography className={statusContainer}>
                Status:{' '}
                <StatusIcon
                  className={classNames({
                    [createdIcon]: status === BetEventStatusesEnum.CREATED,
                    [inProgressIcon]: status === BetEventStatusesEnum.STARTED,
                    [completedIcon]: status === BetEventStatusesEnum.FINISHED,
                  })}
                />
              </Typography>
            </Tooltip>
            <Tooltip title={betEventName} placement="top-start">
              <Typography className={defaultText}>Name: {betEventName}</Typography>
            </Tooltip>
            <Tooltip title={formattedStartDate} placement="top-start">
              <Typography className={defaultText}>Start Date: {formattedStartDate}</Typography>
            </Tooltip>
            <Box className={actionsContainer}>
              <Typography marginLeft="10px" className={defaultText}>
                Settings:
              </Typography>
              <BaseMenu
                menuItems={menuItems}
                menuAnchor={menuAnchor}
                isMenuOpened={isMenuOpened}
                handleOpenMenu={handleOpenMenu}
                handleCloseMenu={handleCloseMenu}
              />
              <BaseCheckbox checked={isActive} onChange={handleActiveEventChange} title="Active" />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <BetSystemEventOutcomes
              betEventId={betEventId}
              betEventName={betEventName}
              isOutcomesExpanded={isOutcomesExpanded}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </Draggable>
  );
};
